import type { Expense, Revenue } from "../../types/startup.types";
import {
  FinancialCalculationService,
  normalizeCustomerDataInput,
  type StandardizedCustomerData,
} from "../../services/FinancialCalculationService";

export { normalizeCustomerDataInput, type StandardizedCustomerData };

/**
 * Calculate the monthly amount for a revenue item at a specific month
 */
export function calculateRevenueAtMonth(
  revenue: Revenue,
  targetDate: Date,
  customerData?: Parameters<typeof normalizeCustomerDataInput>[0],
): number {
  const standardizedRevenue = FinancialCalculationService.standardizeRevenue(revenue);
  const standardizedCustomerData = normalizeCustomerDataInput(customerData);

  const result = FinancialCalculationService.calculateRevenueAtMonth(
    standardizedRevenue,
    targetDate,
    standardizedCustomerData,
  );

  return result.calculatedAmount;
}

/**
 * Calculate the monthly amount for an expense item at a specific month
 */
export function calculateExpenseAtMonth(
  expense: Expense,
  targetDate: Date,
  totalMonthlyRevenue: number = 0,
  customerData?: Parameters<typeof normalizeCustomerDataInput>[0],
): number {
  const standardizedExpense = FinancialCalculationService.standardizeExpense(expense);
  const standardizedCustomerData = normalizeCustomerDataInput(customerData);

  const result = FinancialCalculationService.calculateExpenseAtMonth(
    standardizedExpense,
    targetDate,
    totalMonthlyRevenue,
    standardizedCustomerData,
  );

  return result.calculatedAmount;
}

/**
 * Validate expense configuration
 */
export function validateExpenseConfig(expense: Partial<Expense>): string[] {
  const errors: string[] = [];

  if (!expense.expenseType) {
    errors.push("Expense type is required");
    return errors;
  }

  switch (expense.expenseType) {
    case "fixed":
      if (!expense.amount || expense.amount <= 0) {
        errors.push("Fixed expenses require a positive amount");
      }
      break;

    case "percentage":
      if (!expense.revenueRatio || expense.revenueRatio <= 0 || expense.revenueRatio > 1) {
        errors.push("Percentage expenses require a valid revenue ratio (0-1)");
      }
      break;

    case "hybrid":
      if ((!expense.amount || expense.amount <= 0) && (!expense.revenueRatio || expense.revenueRatio <= 0)) {
        errors.push("Hybrid expenses require either a fixed amount or revenue ratio");
      }
      if (expense.revenueRatio && (expense.revenueRatio <= 0 || expense.revenueRatio > 1)) {
        errors.push("Revenue ratio must be between 0 and 1");
      }
      break;
  }

  return errors;
}

/**
 * Validate revenue configuration
 */
export function validateRevenueConfig(revenue: Partial<Revenue>): string[] {
  const errors: string[] = [];

  if (!revenue.amount || revenue.amount <= 0) {
    errors.push("Revenue amount must be positive");
  }

  if (revenue.isRecurring) {
    if (!revenue.frequency) {
      errors.push("Recurring revenue requires a frequency");
    }
    if (!revenue.startDate) {
      errors.push("Recurring revenue requires a start date");
    }
  }

  return errors;
}
